// =============================================================================
// CallManager — global DM-call coordinator.
// =============================================================================
// Mounted once (inside VoiceStateProvider, within the Router). Owns:
//   - the WS reactions to inbound call.* events,
//   - the incoming/outgoing ringtones,
//   - the incoming-call prompt + outgoing "ringing…" overlay,
//   - the service-worker decline bridge + `?call=<id>` push deep-link.
// All state lives in the store (incomingCall / outgoingCall / activeVoice); the
// imperative transitions live in ../calls.
// =============================================================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useApp } from '../store';
import { wsClient } from '../ws';
import { stopRingtone, startRingtone } from '../sounds';
import { acceptIncomingCall, declineIncomingCall, cancelOutgoingCall } from '../calls';
import { Avatar, Icon } from './ui';

export function CallManager() {
  const navigate = useNavigate();
  const incomingCall = useApp((s) => s.incomingCall);
  const outgoingCall = useApp((s) => s.outgoingCall);
  const [toast, setToast] = useState<string | null>(null);

  // Auto-dismiss the toast.
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [toast]);

  // ---- Inbound call.* events ----
  useEffect(() => {
    return wsClient.on((event) => {
      const st = useApp.getState();
      switch (event.type) {
        case 'call.incoming': {
          // Someone is ringing us. Show the prompt + ring (even if we're busy —
          // "ring anyway" policy; accepting will switch rooms).
          st.setIncomingCall({ dmChannelId: event.dmChannelId, from: event.from });
          startRingtone('incoming');
          break;
        }
        case 'call.ringing':
          // Caller-side "delivered" confirmation — nothing to do (the outgoing
          // overlay is already up from startCall()).
          break;
        case 'call.accepted':
          // Caller: the callee picked up. Stop ringback; stay in the room.
          if (st.outgoingCall?.dmChannelId === event.dmChannelId) {
            stopRingtone();
            st.setOutgoingCall(null);
          }
          break;
        case 'call.declined':
          if (st.outgoingCall?.dmChannelId === event.dmChannelId) {
            stopRingtone();
            st.setOutgoingCall(null);
            if (st.activeVoice?.channelId === event.dmChannelId) st.setActiveVoice(null);
            setToast('Call declined');
          }
          break;
        case 'call.busy':
          // We tried to ring a DM that already has a call in flight. We already
          // optimistically joined the room in startCall(), so just drop the
          // ringback and let the connection settle into the existing call.
          if (st.outgoingCall?.dmChannelId === event.dmChannelId) {
            stopRingtone();
            st.setOutgoingCall(null);
          }
          break;
        case 'call.error':
          if (st.outgoingCall?.dmChannelId === event.dmChannelId) {
            stopRingtone();
            st.setOutgoingCall(null);
            if (st.activeVoice?.channelId === event.dmChannelId) st.setActiveVoice(null);
            setToast(event.error || 'Call failed');
          }
          break;
        case 'call.cancelled':
          // Caller cancelled, or another of our devices answered/declined.
          if (st.incomingCall?.dmChannelId === event.dmChannelId) {
            stopRingtone();
            st.setIncomingCall(null);
          }
          if (st.outgoingCall?.dmChannelId === event.dmChannelId) {
            stopRingtone();
            st.setOutgoingCall(null);
          }
          break;
        case 'call.ended': {
          // Terminal. Clear whichever call state matches and leave the room.
          const dm = event.dmChannelId;
          if (st.incomingCall?.dmChannelId === dm) {
            stopRingtone();
            st.setIncomingCall(null);
          }
          if (st.outgoingCall?.dmChannelId === dm) {
            stopRingtone();
            st.setOutgoingCall(null);
          }
          if (st.activeVoice?.channelId === dm && st.activeVoice.isDm) {
            st.setActiveVoice(null);
            if (event.reason && event.reason !== 'hangup') setToast('Call ended');
          }
          break;
        }
      }
    });
  }, []);

  // ---- Service-worker bridge: decline from a push notification action ----
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    function onMessage(e: MessageEvent) {
      const d = e.data || {};
      if (d.type === 'notif-call-decline' && d.channelId) {
        const st = useApp.getState();
        stopRingtone();
        st.setIncomingCall(null);
        wsClient.send({ type: 'call.decline', channelId: d.channelId });
      } else if (d.type === 'notif-call-accept' && d.channelId) {
        navigate(`/dm/${d.channelId}`);
      }
    }
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  // ---- Push deep-link: /?call=<dmChannelId> opens the DM (modal arrives via WS) ----
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const callDm = params.get('call');
    if (callDm) {
      navigate(`/dm/${callDm}`);
      // Strip the param so a refresh doesn't re-trigger.
      params.delete('call');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, [navigate]);

  function onAcceptIncoming() {
    if (!incomingCall) return;
    const dm = incomingCall.dmChannelId;
    acceptIncomingCall(incomingCall);
    navigate(`/dm/${dm}`);
  }

  return (
    <>
      {incomingCall && (
        <IncomingCallOverlay
          name={incomingCall.from?.displayName || incomingCall.from?.username || 'Someone'}
          avatarUrl={incomingCall.from?.avatarUrl ?? null}
          onAccept={onAcceptIncoming}
          onDecline={() => declineIncomingCall(incomingCall)}
        />
      )}
      {outgoingCall && (
        <OutgoingCallOverlay
          name={outgoingCall.toName}
          onCancel={() => cancelOutgoingCall(outgoingCall.dmChannelId)}
        />
      )}
      {toast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-[calc(1.5rem+env(safe-area-inset-bottom))] z-60 bg-panel text-text text-sm font-semibold px-4 py-2 rounded-full shadow-aim-window border border-border animate-slide-up">
          {toast}
        </div>
      )}
    </>
  );
}

// =============================================================================
// IncomingCallOverlay — full-attention incoming-call prompt.
// =============================================================================
function IncomingCallOverlay({
  name,
  avatarUrl,
  onAccept,
  onDecline,
}: {
  name: string;
  avatarUrl: string | null;
  onAccept: () => void;
  onDecline: () => void;
}) {
  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-fade-in">
      <div className="hybrid-card w-full max-w-xs text-center overflow-hidden">
        <div className="hybrid-card-header justify-center!">Incoming call</div>
        <div className="bg-surface p-6 space-y-4">
          <div className="flex justify-center">
            <div className="relative">
              <span className="absolute inset-0 rounded-full bg-accent/30 animate-ping" />
              <Avatar name={name} url={avatarUrl} size={72} />
            </div>
          </div>
          <div>
            <div className="text-lg font-bold text-text">{name}</div>
            <div className="text-xs text-text-muted">is calling…</div>
          </div>
          <div className="flex items-center justify-center gap-4 pt-1">
            <button
              type="button"
              onClick={onDecline}
              className="flex flex-col items-center gap-1 group"
              title="Decline"
            >
              <span className="w-14 h-14 rounded-full bg-danger text-white flex items-center justify-center shadow-aim-window group-hover:opacity-90 group-active:scale-95 transition">
                <Icon name="phone-off" size={24} />
              </span>
              <span className="text-[11px] text-text-muted">Decline</span>
            </button>
            <button
              type="button"
              onClick={onAccept}
              className="flex flex-col items-center gap-1 group"
              title="Accept"
            >
              <span className="w-14 h-14 rounded-full bg-[#3BA55D] text-white flex items-center justify-center shadow-aim-window group-hover:opacity-90 group-active:scale-95 transition">
                <Icon name="phone" size={24} />
              </span>
              <span className="text-[11px] text-text-muted">Accept</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// OutgoingCallOverlay — "ringing…" with a cancel button (caller side).
// =============================================================================
function OutgoingCallOverlay({ name, onCancel }: { name: string; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-65 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-fade-in">
      <div className="hybrid-card w-full max-w-xs text-center overflow-hidden">
        <div className="hybrid-card-header justify-center!">Calling</div>
        <div className="bg-surface p-6 space-y-4">
          <div className="flex justify-center">
            <div className="relative">
              <span className="absolute inset-0 rounded-full bg-accent/20 animate-ping" />
              <Avatar name={name} url={null} size={64} />
            </div>
          </div>
          <div>
            <div className="text-lg font-bold text-text">{name}</div>
            <div className="text-xs text-text-muted">Ringing…</div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="mx-auto flex flex-col items-center gap-1 group"
            title="Cancel"
          >
            <span className="w-14 h-14 rounded-full bg-danger text-white flex items-center justify-center shadow-aim-window group-hover:opacity-90 group-active:scale-95 transition">
              <Icon name="phone-off" size={24} />
            </span>
            <span className="text-[11px] text-text-muted">Cancel</span>
          </button>
        </div>
      </div>
    </div>
  );
}
